import React from 'react';
import { Box, Switch, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 68,
    height: 34,
    padding: 7,
    cursor: 'pointer',
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        '&.Mui-checked': {
            transform: 'translateX(32px)',
            color: theme.palette.primary.main,
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.primary.light,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 30,
        height: 30,
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        backgroundColor: '#fff',
        border: '1px solid rgba(0,0,0,0.2)',
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        borderRadius: 20 / 2,
        backgroundColor: theme.palette.grey[400],
    },
}));

const SwitchLabel = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
}));

const SwitchTextOn = styled(Typography)(({ theme, checked }) => ({
    position: 'absolute',
    left: '14px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: '#fff',
    visibility: checked ? 'visible' : 'hidden',
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
}));

const SwitchTextOff = styled(Typography)(({ theme, checked }) => ({
    position: 'absolute',
    right: '10px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: '#fff',
    visibility: checked ? 'hidden' : 'visible',
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
}));

const SwitchIconOn = styled(Box)(({ theme, checked }) => ({
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    visibility: checked ? 'visible' : 'hidden',
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    '& svg': {
        fontSize: '1rem',
        color: '#fff',
    },
}));

const SwitchIconOff = styled(Box)(({ theme, checked }) => ({
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    visibility: checked ? 'hidden' : 'visible',
    pointerEvents: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    '& svg': {
        fontSize: '1rem',
        color: '#fff',
    },
}));

/**
 * All / My 토글 (created_by 필터용).
 * orders.myOrderFilter.on = 'My', orders.myOrderFilter.off = 'All'
 * labelOn, labelOff를 넘기면 해당 문구로 표시 (예: W.Req O / W.Req X).
 * iconOn, iconOff를 함께 넘기면 트랙 안에 아이콘으로 표시 (짧은 라벨 대체).
 */
const AllMyToggle = ({ checked, onChange, t, labelOn, labelOff, iconOn, iconOff }) => {
    const { t: trans } = useTranslation();
    const translate = t || trans;
    const onText = labelOn != null ? labelOn : translate('orders.myOrderFilter.on');
    const offText = labelOff != null ? labelOff : translate('orders.myOrderFilter.off');
    const useIcons = iconOn != null && iconOff != null;
    return (
        <SwitchLabel>
            <CustomSwitch
                checked={!!checked}
                onChange={(e) => onChange && onChange(e.target.checked)}
            />
            {useIcons ? (
                <>
                    <SwitchIconOn checked={!!checked}>{iconOn}</SwitchIconOn>
                    <SwitchIconOff checked={!!checked}>{iconOff}</SwitchIconOff>
                </>
            ) : (
                <>
                    <SwitchTextOn checked={!!checked}>
                        {onText}
                    </SwitchTextOn>
                    <SwitchTextOff checked={!!checked}>
                        {offText}
                    </SwitchTextOff>
                </>
            )}
        </SwitchLabel>
    );
};

export default AllMyToggle;
